import { z } from 'zod';
import { supabase } from '@/integrations/supabase/client';

// Validation schema with security constraints
export const contactFormSchema = z.object({
  name: z.string()
    .trim()
    .min(1, { message: "Name is required" })
    .max(100, { message: "Name must be less than 100 characters" })
    .regex(/^[a-zA-Z\s'-]+$/, { message: "Name contains invalid characters" }),
  
  email: z.string()
    .trim()
    .email({ message: "Invalid email address" })
    .max(255, { message: "Email must be less than 255 characters" }),
  
  company: z.string()
    .trim()
    .min(1, { message: "Company is required" })
    .max(100, { message: "Company name must be less than 100 characters" }),
  
  phone: z.string()
    .trim()
    .optional()
    .refine((val) => !val || /^[\+]?[1-9][\d]{0,15}$/.test(val), {
      message: "Invalid phone number format"
    }),
  
  message: z.string()
    .trim()
    .min(10, { message: "Message must be at least 10 characters" })
    .max(1000, { message: "Message must be less than 1000 characters" })
});

export type ContactFormData = z.infer<typeof contactFormSchema>;

// Client-side rate limiting tracker (additional layer of protection)
const submissionTracker = new Map<string, number[]>();

function checkClientRateLimit(identifier: string): boolean {
  const now = Date.now();
  const windowMs = 15 * 60 * 1000; // 15 minutes
  const maxAttempts = 5;

  if (!submissionTracker.has(identifier)) {
    submissionTracker.set(identifier, []);
  }

  const attempts = submissionTracker.get(identifier)!;
  
  // Remove old attempts outside the window
  const recentAttempts = attempts.filter(time => now - time < windowMs);
  submissionTracker.set(identifier, recentAttempts);

  if (recentAttempts.length >= maxAttempts) {
    return false;
  }

  recentAttempts.push(now);
  submissionTracker.set(identifier, recentAttempts);
  return true;
}

export async function submitContactForm(data: ContactFormData) {
  // Validate input data
  const validationResult = contactFormSchema.safeParse(data);
  if (!validationResult.success) {
    throw new Error(`Validation failed: ${validationResult.error.issues.map(e => e.message).join(', ')}`);
  }

  const validatedData = validationResult.data;

  // Client-side rate limiting check (defense in depth)
  const identifier = validatedData.email.toLowerCase();
  if (!checkClientRateLimit(identifier)) {
    throw new Error('Too many submission attempts. Please try again later.');
  }

  // Call edge function instead of exposing API key
  const { data: result, error } = await supabase.functions.invoke('submit-contact', {
    body: {
      name: validatedData.name,
      email: validatedData.email.toLowerCase(),
      company: validatedData.company,
      phone: validatedData.phone || '',
      message: validatedData.message
    }
  });

  if (error) {
    console.error('Contact form submission error');
    throw new Error('Failed to submit contact form. Please try again.');
  }

  if (result?.error) {
    throw new Error(result.error);
  }

  return result;
}
